import { errorMsg } from '../../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Button, TextField, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, Alert, Paper, Grid
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate, useParams } from 'react-router-dom';
import { criarGC, atualizarGC, getGC } from '../../api/gruposCrescimento';
import api from '../../axios';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ContextNavBar from '../../components/ContextNavBar';
import ModuleHeader from '../../components/ModuleHeader';

const DIAS_SEMANA = [
  { value: 'domingo', label: 'Domingo' },
  { value: 'segunda', label: 'Segunda-feira' },
  { value: 'terca', label: 'Terça-feira' },
  { value: 'quarta', label: 'Quarta-feira' },
  { value: 'quinta', label: 'Quinta-feira' },
  { value: 'sexta', label: 'Sexta-feira' },
  { value: 'sabado', label: 'Sábado' },
];

function GCForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const gcId = id || null;
  const isEditing = !!gcId;

  const [membros, setMembros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');
  const [form, setForm] = useState({
    nome: '',
    descricao: '',
    lider_id: '',
    co_lider_id: '',
    dia_semana: '',
    horario: '',
    endereco: '',
    bairro: '',
    anfitriao_nome1: '',
    anfitriao_contato1: '',
    anfitriao_nome2: '',
    anfitriao_contato2: ''
  });

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/secretaria/membros'),
      isEditing ? getGC(gcId) : Promise.resolve(null)
    ])
      .then(([membrosRes, gcData]) => {
        setMembros(membrosRes.data);
        if (gcData) {
          setForm({
            nome: gcData.nome || '',
            descricao: gcData.descricao || '',
            lider_id: gcData.lider_id || '',
            co_lider_id: gcData.co_lider_id || '',
            dia_semana: gcData.dia_semana || '',
            horario: gcData.horario || '',
            endereco: gcData.endereco || '',
            bairro: gcData.bairro || '',
            anfitriao_nome1: gcData.anfitriao_nome1 || '',
            anfitriao_contato1: gcData.anfitriao_contato1 || '',
            anfitriao_nome2: gcData.anfitriao_nome2 || '',
            anfitriao_contato2: gcData.anfitriao_contato2 || ''
          });
        }
      })
      .catch((err) => {
        setErro(errorMsg(err) || 'Erro ao carregar dados');
      })
      .finally(() => setLoading(false));
  }, [gcId, isEditing]);

  const handleChange = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
  };

  const handleSalvar = async () => {
    if (!form.nome.trim()) { setErro('Nome é obrigatório.'); return; }
    if (!form.lider_id) { setErro('Líder é obrigatório.'); return; }
    setSaving(true);
    setErro('');
    try {
      const payload = {
        ...form,
        lider_id: form.lider_id ? parseInt(form.lider_id, 10) : null,
        co_lider_id: form.co_lider_id ? parseInt(form.co_lider_id, 10) : null
      };
      let gc;
      if (isEditing) {
        gc = await atualizarGC(gcId, payload);
      } else {
        gc = await criarGC(payload);
      }
      navigate(`/grupos-crescimento/${gc.id}`);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao salvar GC');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <>
        <Header />
        <ContextNavBar context="grupos_crescimento" />
        <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <ContextNavBar context="grupos_crescimento" />
      <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/grupos-crescimento')} size="small">Voltar</Button>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
        {isEditing ? 'Editar GC' : 'Novo GC'}
      </Typography>

      <Paper sx={{ p: 3, borderRadius: 2 }} elevation={2}>
        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField label="Nome *" value={form.nome} onChange={handleChange('nome')} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Descrição" value={form.descricao} onChange={handleChange('descricao')} fullWidth multiline rows={3} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Líder *</InputLabel>
              <Select value={form.lider_id} label="Líder *" onChange={handleChange('lider_id')}>
                <MenuItem value="">Selecione</MenuItem>
                {membros.map(m => (
                  <MenuItem key={m.id} value={m.id}>{m.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Co-líder</InputLabel>
              <Select value={form.co_lider_id} label="Co-líder" onChange={handleChange('co_lider_id')}>
                <MenuItem value="">Nenhum</MenuItem>
                {membros.map(m => (
                  <MenuItem key={m.id} value={m.id}>{m.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Dia da Semana</InputLabel>
              <Select value={form.dia_semana} label="Dia da Semana" onChange={handleChange('dia_semana')}>
                <MenuItem value="">Selecione</MenuItem>
                {DIAS_SEMANA.map(d => (
                  <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Horário" type="time" value={form.horario} onChange={handleChange('horario')} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Endereço" value={form.endereco} onChange={handleChange('endereco')} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Bairro" value={form.bairro} onChange={handleChange('bairro')} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1, mb: 1 }}>Anfitriões</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Anfitrião 1 — Nome" value={form.anfitriao_nome1} onChange={handleChange('anfitriao_nome1')} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Anfitrião 1 — Contato" value={form.anfitriao_contato1} onChange={handleChange('anfitriao_contato1')} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Anfitrião 2 — Nome" value={form.anfitriao_nome2} onChange={handleChange('anfitriao_nome2')} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Anfitrião 2 — Contato" value={form.anfitriao_contato2} onChange={handleChange('anfitriao_contato2')} fullWidth />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => navigate('/grupos-crescimento')}>Cancelar</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSalvar} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : (isEditing ? 'Atualizar' : 'Salvar')}
          </Button>
        </Box>
      </Paper>
    </Container>
      <Footer />
    </>
  );
}

export default GCForm;
